import { useState, useEffect, useCallback } from "react";
import { LoadPanel } from "devextreme-react";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { getSupportGroups } from "../../../api/support";
import { CustomDataGrid } from "../CustomDataGrid";
import { TeamInfoPane } from "./TeamInfoPane";
import { Team, MOCK_TEAMS } from "./teams-mock";
import "../messages/TicketPage.css";

// Takimlar (destek gruplari) — OrganizationsPage/ContactsPage duzeni:
// ust panelde liste, alt panelde cok-sekmeli (MDI) takim detayi.
type TeamTab = { ref: string; teamRef: string; title: string };
const createTabId = () => `ttab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function TeamsPage() {
    const { t } = useTranslation();

    const [teams, setTeams] = useState<Team[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    const [tabs, setTabs] = useState<TeamTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [topCollapsed, setTopCollapsed] = useState(false);
    const [bottomCollapsed, setBottomCollapsed] = useState(false);

    const activeTab = tabs.find(tb => tb.ref === activeTabId) || null;

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    // ── Load teams ────────────────────────────────────────────────────────────
    // Backend getSupportGroups() bos donerse mock fallback'a duser; dolu donerse
    // ref/ad eslestirilip mock uye/lider verisiyle zenginlestirilir.
    const load = useCallback(() => {
        setLoading(true);
        getSupportGroups(false)
            .then(list => {
                if (list.length) {
                    setTeams(list.map(g => {
                        const mock = MOCK_TEAMS.find(m => m.name.toLowerCase() === g.name.toLowerCase());
                        return {
                            ref: g.ref, name: g.name,
                            description: mock?.description ?? "",
                            lead: mock?.lead ?? "—",
                            members: mock?.members ?? [],
                            openTickets: mock?.openTickets ?? 0,
                            is_active: g.is_active !== false,
                        };
                    }));
                } else {
                    setTeams(MOCK_TEAMS);
                }
            })
            .catch(() => setTeams(MOCK_TEAMS))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Tab yonetimi ──────────────────────────────────────────────────────────
    const openTeamInTab = useCallback((team: Team) => {
        setTabs(prev => {
            const existing = prev.find(tb => tb.teamRef === String(team.ref));
            if (existing) { setActiveTabId(existing.ref); return prev; }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, teamRef: String(team.ref), title: team.name || `#${team.ref}` }];
        });
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setTabs(prev => {
            if (prev.length <= 1) { setActiveTabId(null); return []; }
            const idx = prev.findIndex(tb => tb.ref === tabId);
            const next = prev.filter(tb => tb.ref !== tabId);
            setActiveTabId(cur => cur === tabId ? (next[Math.max(0, idx - 1)] || next[0])?.ref || null : cur);
            return next;
        });
    }, []);

    const selectTeam = useCallback((team: Team) => {
        if (!activeTabId || tabs.length === 0) { openTeamInTab(team); return; }
        setTabs(prev => prev.map(tb => tb.ref === activeTabId
            ? { ...tb, teamRef: String(team.ref), title: team.name || `#${team.ref}` }
            : tb));
    }, [activeTabId, tabs.length, openTeamInTab]);

    const handleRowClick = (data: Team) => selectTeam(data);
    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) selectTeam(e.row.data);
    }, [selectTeam]);

    const columns = [
        { dataField: "name", caption: t("teamName"), width: 200 },
        { dataField: "description", caption: t("description"), width: 300 },
        { dataField: "lead", caption: t("teamLead"), width: 180 },
        {
            dataField: "members", caption: t("members"), width: 110, alignment: "center" as const,
            allowSorting: false,
            calculateCellValue: (row: Team) => row.members.length,
            cellRender: (d: any) => <span>{d.data.members.length}</span>,
        },
        { dataField: "openTickets", caption: t("openTickets"), width: 120, alignment: "center" as const },
        {
            dataField: "is_active", caption: t("status"), width: 110, alignment: "center" as const,
            cellRender: (d: any) => {
                const active = d.value !== false;
                const color = active ? "#10B981" : "#9CA3AF";
                return (
                    <span style={{ background: `${color}1f`, color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                        {active ? t("active") : t("passive")}
                    </span>
                );
            },
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="ticket-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    {/* Üst Pane - Takim listesi */}
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`teams-grid-${gridKey}`}
                            className="custom-grid compact-grid"
                            data={teams}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            t={t}
                            noDataText={t("noDataText")}
                            onRefresh={load}
                            onRowClick={handleRowClick}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            focusedRowKey={activeTab?.teamRef ?? undefined}
                            columnChooserEnabled
                            filterRowVisible={false}
                            rowAlternationEnabled={true}
                            allowColumnReordering
                            exportEnabled
                            additionalToolbarItems={[
                                {
                                    location: "after",
                                    render: () => (
                                        <button type="button" className="splitter-collapse-btn" title={t("closeBottomPane")} onClick={toggleBottom}>
                                            <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </SplitterItem>

                    {/* Alt Pane - Sekmeli takim detayi */}
                    <SplitterItem size="60%" minSize="300px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: "var(--background-color)", padding: "0 12px" }}>
                            {tabs.length > 0 ? (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        {tabs.map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? "active" : ""}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.ref); } }}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => setActiveTabId(tab.ref)}>
                                                    <i className="dx-icon-group detail-header-tab-icon" />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button type="button" className="detail-header-tab-close" onClick={(e) => { e.stopPropagation(); closeTab(tab.ref); }} title={t("closeTab")}>
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" className="splitter-collapse-btn" style={{ marginLeft: "auto" }} title={t("closeTopPane")} onClick={toggleTop}>
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>

                                    <div className="flex-1 min-h-0">
                                        {tabs.map(tab => (
                                            <div key={tab.ref} className="h-full" style={{ display: tab.ref === activeTabId ? "block" : "none" }}>
                                                <TeamInfoPane teamRef={tab.teamRef} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button type="button" className="splitter-collapse-btn" style={{ marginLeft: "auto" }} title={t("closeTopPane")} onClick={toggleTop}>
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: "var(--subtitle-text-color)" }}>
                                        {t("selectTeamForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>
        </div>
    );
}
